import { useLingui } from '@lingui/react/macro';
import { Check } from 'lucide-react';

import { cn } from '@/shared/ui/cn';

import type { FlowTrailState, FlowTrailStep } from './flow-trail';

export interface FlowRailProps {
  readonly steps: readonly FlowTrailStep[];
  readonly className?: string;
}

/**
 * Trilho vertical do Fluxo guiado (U53): ícone, nome e o valor escolhido por etapa; concluída com ✓, atual
 * marcada, e as alcançáveis como botão. Mesmo contrato do `FlowProgress`: `nav` "Etapas", `aria-current="step"` e
 * um texto por etapa para o leitor de tela.
 */
export function FlowRail({ steps, className }: FlowRailProps) {
  const { t } = useLingui();
  const stateText: Readonly<Record<FlowTrailState, string>> = { done: t`concluído`, current: t`atual`, pending: t`não iniciado` };
  return (
    <nav aria-label={t`Etapas`} data-slot="flow-progress" data-orientation="vertical" className={cn('min-w-0', className)}>
      <ol className="flex flex-col gap-0.5">
        {steps.map((step, index) => {
          const Icon = step.icon;
          const first = step.items[0];
          const sub = first ? first.value : step.state === 'pending' ? step.hint : undefined;
          const marker = (
            <span
              aria-hidden
              className={cn(
                'relative inline-flex size-7 shrink-0 items-center justify-center rounded-full border transition-colors duration-(--lume-dur-enter)',
                step.state === 'done' && 'border-transparent bg-ok-wash text-ok',
                step.state === 'current' && 'border-accent bg-accent-wash text-accent-text ring-[3px] ring-accent-wash',
                step.state === 'pending' && 'border-input bg-card text-muted-foreground',
                // O fio até a etapa de cima.
                index > 0 && 'before:absolute before:bottom-full before:left-1/2 before:h-3 before:w-px before:bg-border',
              )}
            >
              {step.state === 'done' ? (
                <Check className="size-3.5" strokeWidth={3} />
              ) : Icon ? (
                <Icon className="size-3.5" />
              ) : (
                <span className="font-mono text-xs font-semibold tabular-nums">{index + 1}</span>
              )}
            </span>
          );
          const text = (
            <span className="flex min-w-0 flex-col pt-0.5">
              <span className="sr-only">{t`Passo ${index + 1} de ${steps.length}: ${step.title}, ${stateText[step.state]}`}</span>
              <span aria-hidden className={cn('truncate text-sm font-medium', step.state === 'pending' ? 'text-muted-foreground' : 'text-foreground')}>
                {step.title}
              </span>
              {sub ? (
                <span className={cn('truncate text-xs', step.state === 'current' ? 'text-accent-text' : 'text-muted-foreground')}>{sub}</span>
              ) : null}
            </span>
          );
          const row = 'grid w-full grid-cols-[1.75rem_minmax(0,1fr)] items-start gap-2.5 rounded-control p-2 text-left';
          return (
            <li key={step.id} {...(step.state === 'current' ? { 'aria-current': 'step' as const } : {})}>
              {step.onSelect ? (
                <button type="button" onClick={step.onSelect} className={cn(row, 'outline-none hover:bg-card focus-visible:ring-2 focus-visible:ring-ring')}>
                  {marker}
                  {text}
                </button>
              ) : (
                <span className={row}>
                  {marker}
                  {text}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
